import React, { useState } from "react";
import { X, Mail, Copy, Check, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "./ui-em";

export default function InviteModal({ open, onClose, defaultRole = "leader", onCreated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [step, setStep] = useState("form"); // form | result
  const [form, setForm] = useState({ name: "", email: "", role: defaultRole });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { user, invitation_url }
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setStep("form");
    setForm({ name: "", email: "", role: defaultRole });
    setResult(null);
    setCopied(false);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", form);
      setResult(data);
      setStep("result");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.invitation_url);
      setCopied(true);
      toast.success("Invitation link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/80 backdrop-blur-md overflow-y-auto">
      <div className="em-card p-6 sm:p-8 w-full max-w-xl my-auto" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
        {step === "form" && (
          <>
            <div className="flex justify-between items-start mb-6 gap-4">
              <div>
                <h3 className="font-display text-xl sm:text-2xl">Invite a leader</h3>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Add a leader from your network. They&apos;ll receive a private link to set their own password.
                </p>
              </div>
              <button onClick={close} className="text-zinc-500 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Full name</label>
                <input required placeholder="Jane Smith" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="em-input" data-testid="invite-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Email</label>
                <input required type="email" placeholder="jane@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="em-input" data-testid="invite-email" />
              </div>
              {isAdmin && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Role</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="em-input">
                    <option value="leader">Leader</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
              <Button type="submit" variant="primary" size="lg" className="w-full" icon={Mail} loading={submitting} data-testid="invite-submit">
                Generate invitation link
              </Button>
            </form>
          </>
        )}

        {step === "result" && result && (
          <>
            <div className="flex justify-between items-start mb-6 gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 mb-4">
                  <Check className="w-3 h-3 text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-semibold">Invitation ready</span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl">
                  Share this link with <span className="text-gold">{result.name}</span>
                </h3>
                <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
                  Send it via email, WhatsApp or any secure channel. Valid for 7 days.
                </p>
              </div>
              <button onClick={close} className="text-zinc-500 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>

            <div className="em-surface rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Invitation link</div>
                <div className="text-xs text-zinc-300 font-mono truncate select-all" data-testid="invite-url">{result.invitation_url}</div>
              </div>
              <button
                data-testid="copy-invite-link"
                onClick={copyLink}
                className="shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-ink-0 font-semibold text-sm hover:bg-gold-hover"
                style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? "Copied" : "Copy link"}</span>
              </button>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-gold/[0.04] border border-gold/15">
              <div className="text-[11px] uppercase tracking-widest text-gold font-semibold mb-2">Suggested message</div>
              <div className="text-sm text-zinc-300 leading-relaxed">
                Hi {result.name?.split(" ")[0]}, welcome to EM Operating System. Click the link below to activate your account and set your password. See you inside 🔥
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2">
              <Button variant="secondary" onClick={() => setStep("form") || setResult(null) || setForm({ name: "", email: "", role: defaultRole })} icon={RefreshCw}>
                Invite another
              </Button>
              <Button variant="primary" onClick={close}>Done</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
